import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

const packages = [
  {
    name: "Starter Team",
    size: "5–15 people",
    description: "Perfect for small businesses, daycares, fitness studios, and community groups needing foundational CPR/AED training.",
    courses: ["CPR / AED", "Basic First Aid"],
    icon: "ri-team-line",
  },
  {
    name: "Healthcare Group",
    size: "5–30 people",
    description: "Designed for hospitals, clinics, and medical offices requiring AHA-aligned BLS, ACLS, or PALS group certification.",
    courses: ["BLS Certification / Renewal", "ACLS or PALS"],
    icon: "ri-hospital-line",
    featured: true,
  },
  {
    name: "Corporate Enterprise",
    size: "30+ people",
    description: "Large-scale training for corporations, government agencies, schools, and emergency service organizations.",
    courses: ["Custom course selection", "Multi-day scheduling", "Volume pricing"],
    icon: "ri-building-2-line",
  },
];

const benefits = [
  { icon: "ri-map-pin-line", title: "On-Site Convenience", desc: "We bring RN instructors directly to your facility — no travel needed for your team." },
  { icon: "ri-calendar-check-line", title: "Flexible Scheduling", desc: "Morning, afternoon, or weekend sessions tailored to your organization&apos;s schedule." },
  { icon: "ri-medal-line", title: "AHA-Aligned Certs", desc: "Every participant receives an American Heart Association–aligned certification card." },
  { icon: "ri-price-tag-3-line", title: "Volume Pricing", desc: "Competitive group rates designed to make team certification cost-effective and accessible." },
  { icon: "ri-nurse-line", title: "RN Instructors", desc: "Clinical expertise in every session — your team learns from real healthcare professionals." },
  { icon: "ri-file-list-3-line", title: "Custom Content", desc: "Course content tailored to your industry — healthcare, corporate, education, or public service." },
];

const clients = [
  "Hospitals & Clinics", "Medical Offices", "Corporate Teams", "Schools & Universities",
  "Fitness Centers", "Law Enforcement", "Fire Departments", "Restaurants & Hospitality",
  "Government Agencies", "Childcare Facilities",
];

export default function GroupTrainingPage() {
  return (
    <main className="pt-20">
      <title>Group CPR Training Kissimmee | Corporate Team Certification | Cardiocraft CPR</title>
      <meta name="description" content="On-site group and corporate CPR training in Kissimmee, FL. We bring RN instructors to your business, hospital, or school. Custom scheduling, AHA-aligned, volume pricing for teams of 5–100+." />
      <meta name="keywords" content="group CPR training Kissimmee, corporate CPR certification Florida, on-site BLS training, team CPR class Kissimmee" />
      <link rel="canonical" href={`${SITE_URL}/group-training`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Group & Corporate CPR Training – Cardiocraft CPR',
            url: `${SITE_URL}/group-training`,
            description: 'On-site or in-facility group CPR certification for businesses, hospitals, schools, and organizations throughout Kissimmee and Central Florida. RN-led, AHA-aligned.',
            provider: {
              '@type': 'EducationalOrganization',
              name: 'Cardiocraft CPR',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '1627 East Vine Street, Suite 203',
                addressLocality: 'Kissimmee',
                addressRegion: 'FL',
                postalCode: '34744',
                addressCountry: 'US',
              },
            },
            areaServed: [
              { '@type': 'City', name: 'Kissimmee' },
              { '@type': 'State', name: 'Florida' },
            ],
          }),
        }}
      />
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1c7a3dfbb4a75664b8624bffc81fc8e5.jpg"
            alt="Group CPR training Kissimmee"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black/90 via-brand-black/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <span className="inline-block border border-brand-gold/50 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              Group &amp; Corporate Training
            </span>
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-6">
              Train Your Entire Team with{" "}
              <span className="text-brand-gold">One Call</span>
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              On-site or in-facility group CPR and life support training for businesses,
              hospitals, schools, and organizations throughout Kissimmee and Central Florida.
              Led by Registered Nurses. AHA-aligned certifications.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="bg-brand-gold text-brand-black font-bold px-8 py-4 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line mr-2" />
                Request a Quote
              </Link>
              <Link
                to="/booking#reserve-spot"
                className="border-2 border-white text-white font-semibold px-8 py-4 rounded-md hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                Book Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 bg-brand-purple-pale">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-brand-purple text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
              Training Packages
            </span>
            <h2 className="font-serif text-4xl font-bold text-brand-black mb-4">
              Group Training Options
            </h2>
            <div className="w-20 h-1 bg-brand-gold mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <div
                key={pkg.name}
                className={`bg-white rounded-xl p-8 border-2 transition-all hover:-translate-y-1 ${
                  pkg.featured ? "border-brand-gold" : "border-gray-100"
                }`}
              >
                {pkg.featured && (
                  <span className="inline-block bg-brand-gold text-brand-black text-xs font-bold uppercase px-3 py-1 rounded-full mb-4">
                    Most Popular
                  </span>
                )}
                <div className="w-14 h-14 flex items-center justify-center bg-brand-purple-pale rounded-xl text-brand-purple mb-5">
                  <i className={`${pkg.icon} text-2xl`} />
                </div>
                <h3 className="font-serif font-bold text-2xl text-brand-black mb-1">{pkg.name}</h3>
                <p className="text-brand-purple text-sm font-semibold mb-4">
                  <i className="ri-group-line mr-1" />
                  {pkg.size}
                </p>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{pkg.description}</p>
                <ul className="space-y-2 mb-6">
                  {pkg.courses.map((c) => (
                    <li key={c} className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-brand-purple" />
                      {c}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    pkg.featured
                      ? "bg-brand-gold text-brand-black hover:bg-brand-gold-dark"
                      : "bg-brand-purple text-white hover:bg-brand-purple-dark"
                  }`}
                >
                  Get a Custom Quote
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-black mb-4">
              Why Organizations Choose Cardiocraft CPR
            </h2>
            <div className="w-20 h-1 bg-brand-gold mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="flex gap-4 p-6 rounded-xl bg-brand-purple-pale border border-brand-purple/10">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-purple rounded-lg text-white flex-shrink-0">
                  <i className={`${b.icon} text-xl`} />
                </div>
                <div>
                  <h3 className="font-semibold text-brand-black text-sm mb-1">{b.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: b.desc.replace(/&apos;/g, "'") }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Train */}
      <section className="py-16 bg-brand-purple">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-8">
            Trusted by Organizations Across Central Florida
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {clients.map((client) => (
              <span
                key={client}
                className="bg-white/10 border border-white/20 text-white text-sm font-medium px-4 py-2 rounded-full"
              >
                {client}
              </span>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-black font-bold px-8 py-4 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line" />
              Request Group Training Quote
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
